import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Heading, Flex, Select, Button, Text, SimpleGrid, Grid,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, useToast,
  Menu, MenuButton, MenuList, MenuItem,
} from '@chakra-ui/react';
import { FiChevronDown, FiDownload } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';
import Page from '../../components/ui/Page';
import SectionCard from '../../components/ui/SectionCard';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import StatusBadge from '../../components/ui/StatusBadge';
import LoadingState from '../../components/ui/LoadingState';
import client from '../../utils/fetchClient';
import { useProjectContext } from '../../context/ProjectContext';
import { CHANGE_LABEL } from './CompareReport';
import { exportCompareReportPdf } from './compareReportPdf';

const PERF_THRESHOLD = 0.25; // ±25% same-status duration change counts as a perf change

const fmtDur = (ms) => {
  if (!ms) return '-';
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
};
const fmtAgo = (iso) => {
  if (!iso) return '';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs > 1 ? 's' : ''} ago`;
  const days = Math.round(hrs / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
};

const CHANGE = {
  'new-failure': { label: 'New failure', fg: 'status.fail', bg: 'status.failBg' },
  fixed: { label: 'Fixed', fg: 'status.pass', bg: 'status.passBg' },
  'still-failing': { label: 'Still failing', fg: 'gray.600', bg: 'gray.100' },
  'status-changed': { label: 'Status changed', fg: 'info.500', bg: 'info.50' },
  'perf-regression': { label: 'Perf regression', fg: 'status.flakyText', bg: 'status.flakyBg' },
  'perf-improvement': { label: 'Perf improvement', fg: 'status.pass', bg: 'status.passBg' },
};

function classify(a, b) {
  if (a && !b) return null; // removed - not a "change" we surface
  if (!a && b) return b.status === 'failed' ? 'new-failure' : null;
  if (a.status === 'passed' && b.status === 'failed') return 'new-failure';
  if (a.status === 'failed' && b.status === 'passed') return 'fixed';
  if (a.status === 'failed' && b.status === 'failed') return 'still-failing';
  if (a.status !== b.status) return 'status-changed';
  // same status - look at duration
  if (a.duration && b.duration) {
    const r = (b.duration - a.duration) / a.duration;
    if (r > PERF_THRESHOLD) return 'perf-regression';
    if (r < -PERF_THRESHOLD) return 'perf-improvement';
  }
  return null;
}

function DeltaTile({ label, a, b, unit = '', lowerBetter = false, plain = false }) {
  const diff = (Number(b) || 0) - (Number(a) || 0);
  const worse = lowerBetter ? diff > 0 : diff < 0;
  const color = diff === 0 ? 'gray.400' : worse ? 'status.fail' : 'status.pass';
  return (
    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="14px" p={4}>
      <Text fontSize="11px" color="gray.500">{label}</Text>
      <Text fontSize="16px" fontWeight="800" color="gray.900" mt={1}>
        {a}{plain ? '' : unit} <Text as="span" color="gray.400" fontWeight="600">→</Text> {b}{plain ? '' : unit}
      </Text>
      {!plain && diff !== 0 && (
        <Text fontSize="11px" fontWeight="700" color={color}>
          {diff > 0 ? '▲' : '▼'} {Math.abs(Math.round(diff * 10) / 10)}{unit}
        </Text>
      )}
    </Box>
  );
}

function CountTile({ label, value, color }) {
  return (
    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="14px" p={4}>
      <Text fontSize="11px" color="gray.500">{label}</Text>
      <Text fontSize="16px" fontWeight="800" mt={1} color={value ? color : 'gray.900'}>{value}</Text>
    </Box>
  );
}

function DurationDelta({ a, b }) {
  const delta = (b ?? 0) - (a ?? 0);
  if (!a && !b) return <Text fontSize="11px" color="gray.400">—</Text>;
  const slower = delta > 0;
  const w = Math.min(50, (Math.abs(delta) / 3600) * 50);
  return (
    <Flex align="center" gap={2}>
      <Box flex="1" h="6px" borderRadius="full" bg="gray.100" position="relative" overflow="hidden">
        <Box position="absolute" top={0} bottom={0} left={slower ? '50%' : `${50 - w}%`} w={`${w}%`} bg={slower ? 'status.fail' : 'status.pass'} borderRadius="full" />
      </Box>
      <Text fontFamily="mono" fontSize="11px" fontWeight="700" color={slower ? 'status.fail' : 'status.pass'} whiteSpace="nowrap">
        {slower ? '+' : '−'}{Math.abs(delta)}
      </Text>
    </Flex>
  );
}

export default function ComparePage() {
  const { effectiveProjectParam } = useProjectContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const [exporting, setExporting] = useState(false);

  const runA = searchParams.get('runA') || '';
  const runB = searchParams.get('runB') || '';
  const [filter, setFilter] = useState('all');

  const [runs, setRuns] = useState([]);
  const [dataA, setDataA] = useState(null);
  const [dataB, setDataB] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const query = { page_size: 50 };
    if (effectiveProjectParam) query.project = effectiveProjectParam;
    client.get('/api/test-runs', { query })
      .then((r) => r.json())
      .then((d) => setRuns(Array.isArray(d) ? d : d?.data || []))
      .catch(() => setRuns([]));
  }, [effectiveProjectParam]);

  useEffect(() => {
    if (!runA) { setDataA(null); return; }
    client.get(`/api/test-runs/${runA}`).then((r) => (r.ok ? r.json() : null)).then(setDataA).catch(() => setDataA(null));
  }, [runA]);
  useEffect(() => {
    if (!runB) { setDataB(null); return; }
    setLoading(true);
    client.get(`/api/test-runs/${runB}`).then((r) => (r.ok ? r.json() : null)).then(setDataB).catch(() => setDataB(null)).finally(() => setLoading(false));
  }, [runB]);

  const setParam = (patch) => {
    const p = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    setSearchParams(p, { replace: true });
  };

  const diff = useMemo(() => {
    const casesA = dataA?.testCases || [];
    const casesB = dataB?.testCases || [];
    if (!casesA.length && !casesB.length) return [];
    const mapA = new Map(casesA.map((c) => [c.title, c]));
    const mapB = new Map(casesB.map((c) => [c.title, c]));
    const titles = new Set([...mapA.keys(), ...mapB.keys()]);
    const out = [];
    titles.forEach((title) => {
      const a = mapA.get(title);
      const b = mapB.get(title);
      const change = classify(a, b);
      if (change) out.push({ title, suite: (b || a).suite || (b || a).file || '', a, b, change });
    });
    return out.sort((x, y) => (x.change === y.change ? 0 : x.change < y.change ? -1 : 1));
  }, [dataA, dataB]);

  const counts = useMemo(() => {
    const c = { all: diff.length };
    for (const r of diff) c[r.change] = (c[r.change] || 0) + 1;
    return c;
  }, [diff]);

  const rows = filter === 'all' ? diff : diff.filter((r) => r.change === filter);

  const tA = dataA?.testRun;
  const tB = dataB?.testRun;
  const rate = (t) => (t?.totalTests ? Math.round((t.passedTests / t.totalTests) * 100) : 0);
  const median = (cases) => {
    const ds = (cases || []).map((c) => c.duration || 0).filter(Boolean).sort((a, b) => a - b);
    return ds.length ? ds[Math.floor(ds.length / 2)] : 0;
  };

  const summaryRows = useMemo(() => {
    if (!tA || !tB) return [];
    const mkDelta = (d, unit = '', bad = 'up') => {
      if (d === 0) return { delta: 'no change', deltaColor: 'gray.400' };
      const isBad = bad === 'up' ? d > 0 : d < 0;
      return { delta: `${d > 0 ? '+' : ''}${d}${unit}`, deltaColor: isBad ? 'status.fail' : 'status.pass' };
    };
    return [
      { label: 'Pass rate', a: `${rate(tA)}%`, b: `${rate(tB)}%`, ...mkDelta(rate(tB) - rate(tA), ' pts', 'down') },
      { label: 'Passed', a: tA.passedTests, b: tB.passedTests, ...mkDelta(tB.passedTests - tA.passedTests, '', 'down') },
      { label: 'Failed', a: tA.failedTests, b: tB.failedTests, ...mkDelta(tB.failedTests - tA.failedTests, '', 'up') },
      { label: 'Median duration', a: `${median(dataA?.testCases)} ms`, b: `${median(dataB?.testCases)} ms`, ...mkDelta(median(dataB?.testCases) - median(dataA?.testCases), ' ms', 'up') },
      { label: 'New failures', a: '—', b: counts['new-failure'] || 0, delta: '', deltaColor: 'gray.400' },
      { label: 'Perf regressions', a: '—', b: counts['perf-regression'] || 0, delta: '', deltaColor: 'gray.400' },
      { label: 'Fixed', a: '—', b: counts.fixed || 0, delta: '', deltaColor: 'gray.400' },
    ];
  }, [tA, tB, dataA, dataB, counts]);

  const FILTERS = [
    { key: 'all', label: `All changes ${counts.all || 0}` },
    { key: 'new-failure', label: `New failures ${counts['new-failure'] || 0}` },
    { key: 'fixed', label: `Fixed ${counts.fixed || 0}` },
    { key: 'perf-regression', label: `Regressions ${counts['perf-regression'] || 0}` },
    { key: 'perf-improvement', label: `Improvements ${counts['perf-improvement'] || 0}` },
    { key: 'still-failing', label: `Still failing ${counts['still-failing'] || 0}` },
    { key: 'status-changed', label: `Status changed ${counts['status-changed'] || 0}` },
  ];

  const exportExcel = async () => {
    setExporting(true);
    try {
      const XLSX = await import('xlsx'); // lazy: keeps xlsx out of the main bundle
      const aoa = [
        ['Test', 'Suite', 'Run A status', 'Run B status', 'Run A ms', 'Run B ms', 'Delta ms', 'Change'],
        ...diff.map((r) => [
          r.title, r.suite, r.a?.status || '', r.b?.status || '',
          r.a?.duration ?? '', r.b?.duration ?? '',
          (r.b?.duration ?? 0) - (r.a?.duration ?? 0),
          CHANGE_LABEL[r.change],
        ]),
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
        ['Metric', 'Run A', 'Run B'],
        ...summaryRows.map((s) => [s.label, s.a, s.b]),
      ]), 'Summary');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), 'Changes');
      XLSX.writeFile(wb, `compare-${String(runA).slice(0, 6)}-${String(runB).slice(0, 6)}.xlsx`);
    } catch {
      toast({ title: 'Excel export failed', status: 'error', duration: 2500 });
    } finally {
      setExporting(false);
    }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      await exportCompareReportPdf({
        project: runs[0]?.Project?.name || null,
        runA: tA,
        runB: tB,
        summary: summaryRows,
        rows: diff,
      });
    } catch {
      toast({ title: 'PDF export failed', status: 'error', duration: 2500 });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Page maxW="7xl">
      <Breadcrumbs items={[{ label: 'Metrics', to: '/metrics' }, { label: 'Compare runs' }]} />
      <Box mb={5}>
        <Heading fontWeight="800" letterSpacing="-0.5px" size="lg" color="gray.900">Compare runs</Heading>
        <Text fontSize="sm" color="gray.500">What changed between two runs — new failures, fixes, and duration regressions or improvements.</Text>
      </Box>

      <Grid templateColumns={{ base: '1fr', md: '1fr 40px 1fr' }} gap={3.5} alignItems="center" mb={5}>
        {[['Run A', 'baseline', runA, (id) => setParam({ runA: id }), tA], null, ['Run B', 'candidate', runB, (id) => setParam({ runB: id }), tB]].map((p, i) => {
          if (!p) return <Text key="vs" textAlign="center" color="gray.400" fontWeight="700" fontSize="sm" display={{ base: 'none', md: 'block' }}>vs</Text>;
          const [tag, tone, val, onChange, meta] = p;
          return (
            <Box key={tag} bg="white" border="1px solid" borderColor="gray.200" borderRadius="14px" p={4}>
              <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.05em" color="gray.400" mb={2}>{tag} · {tone}</Text>
              <Select value={val || ''} onChange={(e) => onChange(e.target.value || undefined)} placeholder="Select a run" size="sm" borderRadius="full" bg="white">
                {runs.map((r) => <option key={r.id} value={r.id}>{r.name || 'Run'} #{String(r.id).slice(0, 8)}</option>)}
              </Select>
              {meta && (
                <Text fontSize="11.5px" color="gray.400" fontFamily="mono" mt={2}>
                  {[meta.branch && `${meta.branch}${meta.commit ? ` @ ${String(meta.commit).slice(0, 8)}` : ''}`, meta.environment, fmtAgo(meta.startTime)].filter(Boolean).join(' · ')}
                </Text>
              )}
            </Box>
          );
        })}
      </Grid>

      {!runA || !runB ? (
        <SectionCard><Text color="gray.500" fontSize="sm">Select both Run A and Run B to see the comparison.</Text></SectionCard>
      ) : loading ? (
        <LoadingState message="Loading comparison" />
      ) : (
        <>
          <SimpleGrid columns={{ base: 2, md: 6 }} spacing={3.5} mb={5}>
            <DeltaTile label="Pass rate" a={rate(tA)} b={rate(tB)} unit="%" />
            <DeltaTile label="Failed" a={tA?.failedTests || 0} b={tB?.failedTests || 0} lowerBetter />
            <DeltaTile label="Median dur" a={fmtDur(median(dataA?.testCases))} b={fmtDur(median(dataB?.testCases))} plain />
            <CountTile label="New failures" value={counts['new-failure'] || 0} color="status.fail" />
            <CountTile label="Perf regressions" value={counts['perf-regression'] || 0} color="status.flakyText" />
            <CountTile label="Fixed" value={counts.fixed || 0} color="status.pass" />
          </SimpleGrid>

          <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="16px" overflow="hidden">
            <Flex align="center" gap={1.5} p={4} wrap="wrap">
              {FILTERS.map((f) => {
                const active = f.key === filter;
                return (
                  <Button key={f.key} size="xs" onClick={() => setFilter(f.key)} borderRadius="full" variant="outline"
                    fontWeight={active ? '700' : '600'} color={active ? 'brand.600' : 'gray.500'}
                    bg={active ? 'brand.50' : 'white'} borderColor={active ? 'brand.600' : 'gray.200'}>
                    {f.label}
                  </Button>
                );
              })}
              <Box flex="1" />
              <Menu>
                <MenuButton as={Button} size="xs" variant="outline" borderRadius="full" leftIcon={<FiDownload />} rightIcon={<FiChevronDown />} isLoading={exporting}>
                  Export
                </MenuButton>
                <MenuList>
                  <MenuItem onClick={exportExcel} isDisabled={!diff.length}>Excel (.xlsx)</MenuItem>
                  <MenuItem onClick={exportPdf} isDisabled={!diff.length}>PDF report</MenuItem>
                </MenuList>
              </Menu>
            </Flex>

            {rows.length === 0 ? (
              <Box px={4} pb={5}><Text fontSize="sm" color="gray.400">No changes in this filter.</Text></Box>
            ) : (
              <TableContainer>
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Test</Th><Th>Suite</Th><Th>Run A</Th><Th>Run B</Th>
                      <Th isNumeric>A · ms</Th><Th isNumeric>B · ms</Th><Th w="160px">Δ duration</Th><Th>Change</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.map((r) => (
                      <Tr key={r.title}>
                        <Td fontWeight="600" color="gray.900" maxW="300px" whiteSpace="normal">{r.title}</Td>
                        <Td color="gray.500">{r.suite}</Td>
                        <Td>{r.a ? <StatusBadge status={r.a.status} /> : <Text fontSize="xs" color="gray.400">—</Text>}</Td>
                        <Td>{r.b ? <StatusBadge status={r.b.status} /> : <Text fontSize="xs" color="gray.400">—</Text>}</Td>
                        <Td isNumeric fontFamily="mono" fontSize="12px" color="gray.500">{r.a?.duration ?? '—'}</Td>
                        <Td isNumeric fontFamily="mono" fontSize="12px" color="gray.500">{r.b?.duration ?? '—'}</Td>
                        <Td><DurationDelta a={r.a?.duration} b={r.b?.duration} /></Td>
                        <Td>
                          <Box as="span" fontSize="11px" fontWeight="700" color={CHANGE[r.change].fg} bg={CHANGE[r.change].bg} px={2.5} py={1} borderRadius="full">
                            {CHANGE[r.change].label}
                          </Box>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </>
      )}
    </Page>
  );
}
